import { formatTs } from '@siliconx/shared/ui/format';
import { ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP } from '@siliconx/shared';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { AvoidedEventView } from '../../hooks/useEnvironmentalOverview';
import { formatTonnes } from './format';

/**
 * SIL-472/SIL-471 — recent avoided-emissions events tape. HYBRID: real rows
 * (one per the org's COMPLETED `marketplace_transaction`) when the org has
 * at least one, SAMPLE fallback otherwise — see
 * `hooks/useEnvironmentalOverview.ts`'s `useAvoidedEventsTape`. Advisory by
 * construction either way — a kg CO2e figure here, never summed with or
 * drawn against the KPI strip's footprint numbers.
 *
 * `verified` (real rows only, when the transaction's `passport_artifact` is
 * linked) renders a small "Verified" chip — attests the TRANSFER happened
 * and is passport-matched, never the avoided-kgCO2e ESTIMATE next to it (see
 * the tooltip's exact wording, `ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP`).
 */
export function AvoidedEventsTape({ events }: { events: AvoidedEventView[] }) {
  if (events.length === 0) {
    return <div className="py-6 text-center text-xs text-zinc-400">No avoided-emissions events yet.</div>;
  }

  return (
    <div className="flex flex-col">
      {events.map((e) => (
        <div key={e.id} className="flex items-center gap-3.5 border-b border-zinc-100 py-2 text-sm last:border-0">
          <span className="w-24 shrink-0 font-mono text-[13px] text-zinc-500">{formatTs(e.timestamp)}</span>
          <span className="w-24 shrink-0 text-xs text-zinc-500">{e.assetClass}</span>
          <span className="flex flex-1 items-center gap-2 truncate">
            <span className="truncate">
              <span className="font-medium">{e.model}</span> · {e.event}
            </span>
            {e.verified && (
              <StatusPill tone="green" title={ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP} className="shrink-0">
                Verified
              </StatusPill>
            )}
          </span>
          <span className="w-20 shrink-0 text-right font-mono text-sm font-semibold tabular-nums text-sky-700">
            −{formatTonnes(e.avoidedKgco2e)}
          </span>
        </div>
      ))}
    </div>
  );
}
